import { useEffect, useMemo, useState } from 'react';
import { Package, PencilLine, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { Table, type Column } from '@/components/ui/Table';
import { ROLES } from '@/constants/roles';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { CatalogueFeature } from '@/types/platformFeature';
import { DeckPageHeader } from '../../components/DeckPageHeader';
import { featuresApi } from '../api/features.api';
import { CatalogueEntryModal } from '../components/CatalogueEntryModal';
import { FeatureBuyersPanel } from '../components/FeatureBuyersPanel';

/**
 * The catalogue itself: every feature the product offers, what it costs, and
 * which customers have it.
 *
 * The Feature tracker looks at one customer at a time; this looks at one
 * feature at a time — "who has bought fee reminders?" is asked as often as
 * "what has this school bought?", and answering it from the tracker meant
 * opening every customer in turn.
 *
 * Support may read all of it; only a SUPER_ADMIN may add, change or delete,
 * which every write endpoint enforces on its own as well.
 */
export function FeatureCataloguePage() {
  const role = useAuthStore((state) => state.user?.role);
  const canEdit = role === ROLES.SUPER_ADMIN;

  const { data, isLoading, error, refetch } = useApiQuery(() => featuresApi.catalogue(), []);
  const features = useMemo(() => data ?? [], [data]);

  /** The feature whose customers are listed beside the table. */
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  // Settles on the first row, and moves off one that has just been deleted.
  useEffect(() => {
    if (features.length === 0) setSelectedUuid(null);
    else if (!selectedUuid || !features.some((feature) => feature.uuid === selectedUuid)) {
      setSelectedUuid(features[0].uuid);
    }
  }, [features, selectedUuid]);
  const selected = features.find((feature) => feature.uuid === selectedUuid) ?? null;

  // --- add and edit ---------------------------------------------------------
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<CatalogueFeature | null>(null);

  function openForm(feature: CatalogueFeature | null) {
    setEditing(feature);
    setIsFormOpen(true);
  }

  // --- delete ---------------------------------------------------------------
  const [deleting, setDeleting] = useState<CatalogueFeature | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const deleteBlocked = (deleting?.instituteCount ?? 0) > 0;

  async function confirmDelete() {
    if (!deleting) return;
    setIsDeleting(true);
    try {
      await featuresApi.removeFromCatalogue(deleting.uuid);
      toast.success(`${deleting.name} deleted`, 'It no longer appears on any tracker.');
      setDeleting(null);
      refetch();
    } catch (caught) {
      // The server names the reason — usually customers who still have it.
      toast.error(
        'Could not delete that feature',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsDeleting(false);
    }
  }

  const columns: Column<CatalogueFeature>[] = [
    {
      key: 'feature',
      header: 'Feature',
      render: (feature) => (
        <div className="flex min-w-0 flex-col gap-0.5">
          <span
            className={cn(
              'truncate text-sm font-[560]',
              feature.uuid === selectedUuid ? 'text-accent' : 'text-ink',
            )}
          >
            {feature.name}
          </span>
          <span className="truncate font-mono text-2xs text-ink-3">{feature.key}</span>
        </div>
      ),
    },
    {
      key: 'price',
      header: 'Price',
      align: 'right',
      render: (feature) => (
        <span
          className={cn('text-sm tabular-nums', feature.price === null ? 'text-ink-3' : 'text-ink')}
        >
          {feature.price === null ? 'Base licence' : formatCurrency(feature.price)}
        </span>
      ),
    },
    {
      key: 'buyers',
      header: 'Requested by',
      align: 'right',
      render: (feature) => (
        <span className="flex flex-col items-end">
          <span
            className={cn(
              'text-sm tabular-nums',
              feature.instituteCount === 0 ? 'text-ink-3' : 'text-ink',
            )}
          >
            {feature.instituteCount} institute{feature.instituteCount === 1 ? '' : 's'}
          </span>
          {feature.instituteCount > 0 && (
            <span className="text-xs tabular-nums text-ink-3">
              Active at {feature.enabledInstituteCount}
            </span>
          )}
        </span>
      ),
    },
    ...(canEdit
      ? [
          {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            align: 'right' as const,
            render: (feature: CatalogueFeature) => (
              // The row itself selects; these must not also select it.
              <div
                className="flex justify-end gap-1.5"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
                role="presentation"
              >
                <Button
                  variant="secondary"
                  size="sm"
                  iconOnly
                  aria-label={`Edit ${feature.name}`}
                  title="Edit"
                  onClick={() => openForm(feature)}
                >
                  <PencilLine className="h-3.5 w-3.5" aria-hidden />
                </Button>
                <Button
                  variant="dangerSoft"
                  size="sm"
                  iconOnly
                  aria-label={`Delete ${feature.name}`}
                  title="Delete"
                  onClick={() => setDeleting(feature)}
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-6">
      <DeckPageHeader
        eyebrow="Platform"
        title="Feature catalogue"
        description="Everything the product offers, what each feature costs, and which schools and colleges have it."
        actions={
          canEdit && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => openForm(null)}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              New feature
            </Button>
          )
        }
      />

      {error ? (
        <div className="card p-5">
          <QueryError title="Could not load the catalogue" error={error} onRetry={refetch} />
        </div>
      ) : (
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]">
          <section className="card min-w-0 p-0" aria-label="Features">
            <Table
              columns={columns}
              rows={features}
              rowKey={(feature) => feature.uuid}
              isLoading={isLoading}
              onRowClick={(feature) => setSelectedUuid(feature.uuid)}
              empty={
                <EmptyState
                  icon={<Package className="h-6 w-6" />}
                  title="The catalogue is empty"
                  description={
                    canEdit
                      ? 'Add the first feature the product offers.'
                      : 'A super administrator adds features here.'
                  }
                  action={
                    canEdit && (
                      <Button size="sm" onClick={() => openForm(null)} leftIcon={<Plus className="h-4 w-4" />}>
                        New feature
                      </Button>
                    )
                  }
                />
              }
            />
          </section>

          <FeatureBuyersPanel feature={selected} className="lg:sticky lg:top-4" />
        </div>
      )}

      <CatalogueEntryModal
        isOpen={isFormOpen}
        feature={editing}
        onClose={() => setIsFormOpen(false)}
        onSaved={refetch}
      />

      <ConfirmDialog
        isOpen={deleting !== null}
        title={deleting ? `Delete ${deleting.name}?` : 'Delete feature?'}
        description={
          deleteBlocked
            ? `${deleting?.instituteCount} institute${deleting?.instituteCount === 1 ? ' has' : 's have'} this feature. Remove it from their trackers first — then it can be deleted.`
            : 'It disappears from the catalogue and every tracker. Its history is kept for the audit trail.'
        }
        confirmLabel="Delete feature"
        tone="danger"
        isLoading={isDeleting}
        isConfirmDisabled={deleteBlocked}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}
